/**
 * Dashboard Service Layer
 *
 * This service provides API methods for dashboard-related operations:
 * 1. getDashboardSummary - Fetch dashboard summary with Row 2 filters
 * 2. getStudentAttendance - Fetch student attendance with Row 4 date range
 *
 * Used by Redux async thunks in dashboard.action.ts
 *
 * @module dashboard.service
 */

import { AxiosResponse } from 'axios';
import api from '../api';
import {
  IGetDashboardSummaryRequestParams,
  IGetStudentAttendanceRequestParams,
  IDashboardSummaryResponse,
} from '../../interface/dashboard.interface';

/**
 * Get Dashboard Summary with Row 2 Filters
 *
 * Fetches dashboard summary data including:
 * - activeStudents (filterable by Row 2)
 * - activeTeachers (NOT filterable - global)
 * - activeStaff (NOT filterable - global)
 * - pendingInvoices (filterable by Row 2)
 * - upcomingEventsCount (filterable by Row 2)
 *
 * Endpoint: GET /v2/dashboard
 * Query Params: curriculumId, gradeId, classroomId (optional)
 *
 * @param params - Row 2 filter parameters (already typed and validated at page level)
 * @returns Promise<AxiosResponse<IDashboardSummaryResponse>>
 */
export const getDashboardSummary = async (
  params: IGetDashboardSummaryRequestParams = {},
): Promise<AxiosResponse<IDashboardSummaryResponse>> => {
  return await api.get('/dashboard', { params });
};

/**
 * Get Student Attendance with Row 2 Filters + Row 4 Date Range
 *
 * Fetches student attendance data combining:
 * - Row 2 filters: curriculum/grade/classroom (optional)
 * - Row 4 filter: date range (optional)
 *
 * Endpoint: GET /v2/dashboard
 * Query Params: curriculumId, gradeId, classroomId, startDate, endDate (all optional)
 *
 * @param params - Combined Row 2 filters and Row 4 date range (already typed and validated at page level)
 * @returns Promise<AxiosResponse<IDashboardSummaryResponse>>
 */
export const getStudentAttendance = async (
  params: IGetStudentAttendanceRequestParams = {},
): Promise<AxiosResponse<IDashboardSummaryResponse>> => {
  return await api.get('/dashboard', { params });
};
